import * as _ from "lodash";
import { select, type Selection } from "d3-selection";
import { hierarchy, tree as d3tree, type HierarchyPointNode, type HierarchyPointLink, type TreeLayout } from "d3-hierarchy";
import { zoom as d3zoom, zoomIdentity, zoomTransform, type ZoomBehavior, type D3ZoomEvent } from "d3-zoom";
import "d3-transition"; // Side-effect import: augments the d3 selection prototype with `.transition()`.
import type { ValueDriverTreeNode, ValueDriverTreeNodeField } from "./VdtNode";

const COLLAPSE_EXPAND_BUTTON_OVERFLOW_BOTTOM = 12;

// The tree layout wraps each data node; we also stash the previous position on the DATA itself
// (`x0`/`y0`) so entering nodes can animate out from their parent's prior position.
type VdtData<TModel> = ValueDriverTreeNode<TModel> & { x0?: number; y0?: number };
type VdtNode<TModel> = HierarchyPointNode<VdtData<TModel>>;
type VdtLink<TModel> = HierarchyPointLink<VdtData<TModel>>;

// The elbow path generators are called both with laid-out link endpoints (HierarchyPointNode, which has
// `.children`) and with synthetic `{ x, y }` points during enter/exit transitions.
interface ElbowEnds {
    source: { x: number; y: number; children?: unknown[] };
    target: { x: number; y: number };
}

export interface ValueDriverTreeOptions {
    width?: number;
    height?: number;
    nodeWidth?: number;
    nodeHeight?: number;
    boxRadius?: number;
    siblingSpacing?: number;
    parentSpacing?: number;
    neighbourSpacingFactor?: number;
    onExpand?: (node: ValueDriverTreeNode<any>) => void;
    onCollapse?: (node: ValueDriverTreeNode<any>) => void;
    onZoomLevelChanged?: (zoomLevel: number) => void;
    onFieldValueEdited?: ({ node, field }: { node: ValueDriverTreeNode<any>, field: ValueDriverTreeNodeField }) => void;
    onNodeClick?: (node: ValueDriverTreeNode<any>) => void;
    onNodeTitleClick?: (node: ValueDriverTreeNode<any>) => void;
    duration?: number;
    horizontalLayout?: boolean;
    displayModeTransitionLevel?: number;
    zoomToFitOnNodeToggle?: boolean;
    showProgressBarInSimpleMode?: boolean;
    staticPlot?: boolean;
    defaultCustomNodeTemplate?: string;
}


export class ValueDriverTree<TModel> {
    public root: ValueDriverTreeNode<TModel>;
    private tree: TreeLayout<VdtData<TModel>>;
    private svg: Selection<SVGSVGElement, unknown, null, undefined>;
    private container: Selection<SVGGElement, unknown, null, undefined>;
    public width: number;
    public height: number;
    private nodeWidth: number;
    private nodeHeight: number;
    private siblingSpacing: number;
    private parentSpacing: number;
    private neighbourSpacingFactor: number;
    private displayModeTransitionLevel: number;
    public mode: "full" | "simple";
    public onExpand: (node: ValueDriverTreeNode<any>) => void;
    public onCollapse: (node: ValueDriverTreeNode<any>) => void;
    public onZoomLevelChanged: (newZoomLevel: number) => void;
    public onFieldValueEdited?: ({ node, field }: { node: ValueDriverTreeNode<any>, field: ValueDriverTreeNodeField }) => void;
    public onNodeClick?: (node: ValueDriverTreeNode<any>) => void;
    public onNodeTitleClick?: (node: ValueDriverTreeNode<any>) => void;
    public duration: number;
    private zoomListener: ZoomBehavior<SVGSVGElement, unknown>;
    private lastClickTarget?: ValueDriverTreeNode<TModel>;
    public scale: number;
    public zoomToFitOnNodeToggle: boolean;


    constructor(
        private selector: string | Element,
        private options: ValueDriverTreeOptions,
    ) {
        this.width = options.width || 800;
        this.height = options.height || 600;
        this.nodeWidth = options.nodeWidth || 200;
        this.nodeHeight = options.nodeHeight || 120;
        this.siblingSpacing = options.siblingSpacing || (options.horizontalLayout ? 20 : 40);
        this.parentSpacing = options.parentSpacing || (options.horizontalLayout ? 100 : 40);
        this.parentSpacing = this.parentSpacing < 40 ? 40 : this.parentSpacing;
        this.neighbourSpacingFactor = options.neighbourSpacingFactor || 0.2;
        this.onExpand = options.onExpand;
        this.onCollapse = options.onCollapse;
        this.onZoomLevelChanged = options.onZoomLevelChanged;
        this.onFieldValueEdited = options.onFieldValueEdited;
        this.onNodeClick = options.onNodeClick;
        this.onNodeTitleClick = options.onNodeTitleClick;
        this.duration = options.duration ?? 750;// duration of transitions in ms
        this.displayModeTransitionLevel = options.displayModeTransitionLevel ?? 0.8;
        this.mode = "simple" // This will get overridden by the zoom level anyway, but we need a default value for the initial layout...
        this.zoomToFitOnNodeToggle = options.zoomToFitOnNodeToggle ?? true;
        this.zoomListener = d3zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.1, 8])
            // Provide the viewport extent explicitly from the known pixel dimensions. The svg is sized
            // with width/height="100%", so d3-zoom's default extent (which reads svg.width.baseVal) would
            // be unreliable; using this.width/this.height keeps it correct as the container is resized.
            .extent(() => [[0, 0], [this.width, this.height]] as [[number, number], [number, number]])
            .on("zoom", (event: D3ZoomEvent<SVGSVGElement, unknown>) => this.zoom(event));

        this.initTree();

        // All translations are centered around the center point of the root node of the tree.
        // In v3 the initial pan lived on the zoom behaviour; in modern d3-zoom the transform lives on the
        // DOM node, so we seed it here (at scale 1) by pushing an initial transform onto the svg selection.
        const [initialX, initialY] = options.horizontalLayout
            ? [this.nodeWidth / 2, this.height / 2]
            : [this.width / 2, this.nodeHeight / 2];
        this.applyTransform(initialX, initialY, 1);
    }

    private initTree() {
        // `selector` may be a string or an Element; d3.select handles both at runtime. Callers always pass
        // an Element, so we narrow to that for typing purposes.
        const host = select(this.selector as Element);
        host.selectAll("svg").remove();

        this.svg = host
            .append<SVGSVGElement>("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .call(this.zoomListener);

        this.container = this.svg.append<SVGGElement>("g");

        const nodeSize: [number, number] = this.options.horizontalLayout
            ? [this.nodeHeight + this.siblingSpacing, this.nodeWidth + this.parentSpacing]
            : [this.nodeWidth + this.siblingSpacing, this.nodeHeight + this.parentSpacing];

        // NB: the children accessor (collapse handling) now lives on `d3.hierarchy` (see `computeLayout`),
        // since d3.tree() no longer accepts a `.children()` accessor in the modular API.
        this.tree = d3tree<VdtData<TModel>>()
            .nodeSize(nodeSize)
            .separation((a, b) => { return a.parent === b.parent ? 1 : (1 + this.neighbourSpacingFactor); });
    }

    // Builds a fresh hierarchy from the (mutable) data model and runs the tree layout over it.
    // Unlike the old d3.layout.tree(), d3.hierarchy() does NOT mutate the data — it returns wrapper
    // nodes whose datum is under `.data`, so domain fields are read via `node.data.*` and positional
    // fields (`.x`/`.y`/`.parent`/`.children`) live on the wrapper.
    private computeLayout(): VdtNode<TModel> {
        const root = hierarchy<VdtData<TModel>>(this.root, node => (node.collapsed ? null : node.nodes));
        return this.tree(root);
    }

    // Resolves the caller-supplied data node to the wrapper for the same id in the freshly computed
    // layout, falling back to the layout root.
    private resolveSource(layoutNodes: VdtNode<TModel>[], source: ValueDriverTreeNode<TModel>): VdtNode<TModel> {
        return layoutNodes.find(n => n.data.id === source?.id) ?? layoutNodes[0];
    }

    private redrawTreeInNewMode(mode: "full" | "simple") {
        this.mode = mode;

        if (!this.root) return;

        // The draw function is normally used for collapse/expand redraws, but we're (ab)using it here to redraw the tree when the display mode changes.
        // If this call happens during a collapse/expand animation, the "parent" for the animation will be changed if we just pass in an arbitrary node,
        // so we store the last click target in the toggle handler and use it here, with root as a fallback...
        this.draw(this.lastClickTarget ?? this.root);
    }

    private zoom(event: D3ZoomEvent<SVGSVGElement, unknown>) {
        const scale = event.transform.k;

        // Notify subscribers if zoom level has changed...
        if (this.scale !== scale) {
            this.onZoomLevelChanged && this.onZoomLevelChanged(scale);
            this.scale = scale;
        }

        if (this.mode === "simple" && scale >= this.displayModeTransitionLevel) {
            this.redrawTreeInNewMode("full");
        } else if (this.mode === "full" && scale < this.displayModeTransitionLevel) {
            this.redrawTreeInNewMode("simple");
        }

        // A ZoomTransform stringifies to "translate(x,y) scale(k)", matching the old hand-built string.
        this.container.attr("transform", event.transform.toString());
    }

    // Pushes a transform onto the svg (the element the zoom behaviour is bound to), which fires the
    // "zoom" handler above. This replaces the old `zoomListener.scale()/.translate()/.event()` trio.
    private applyTransform(x: number, y: number, scale: number) {
        this.zoomListener.transform(this.svg, zoomIdentity.translate(x, y).scale(scale));
    }

    private currentScale(): number {
        return zoomTransform(this.svg.node()!).k;
    }

    public zoomOut() {
        this.zoomTo(this.currentScale() - 0.25);
    }

    public zoomIn() {
        this.zoomTo(this.currentScale() + 0.25);
    }

    public zoomTo(scale: number) {
        let extent = this.zoomListener.scaleExtent();

        if (scale < extent[0] || scale > extent[1]) return;

        // Preserve the current pan; only change the scale.
        const t = zoomTransform(this.svg.node()!);
        this.applyTransform(t.x, t.y, scale);
    }

    public zoomToFit() {
        let allNodes = this.computeLayout().descendants();
        let xMax = _.chain(allNodes).map(n => n.x + this.nodeWidth / 2).max().value();
        let xMin = _.chain(allNodes).map(n => n.x - this.nodeWidth / 2).min().value();
        let yMax = _.chain(allNodes).map(n => n.y + this.nodeHeight / 2).max().value() + COLLAPSE_EXPAND_BUTTON_OVERFLOW_BOTTOM;
        let yMin = _.chain(allNodes).map(n => n.y - this.nodeHeight / 2).min().value();
        let yExtent = yMax - yMin;
        let xExtent = xMax - xMin;

        let scale = Math.min(this.width / (xMax - xMin), this.height / (yMax - yMin)) * 0.95; // Leave 5% whitespace on the sides after zooming...

        let translateX: number;
        let translateY: number;
        if (this.options.horizontalLayout) {
            translateX = this.nodeWidth / 2 * scale;
            translateY = this.height / 2;
        }
        else {
            // If the scaling is constrained by the width, center the chart vertically.  We do this by moving the chart down by (open space / 2), where open space = container height - scaled chart height.
            // If constrained by the height, just move the top node fully into view (box height / 2)...
            translateX = xExtent * scale < this.width ? (this.width / scale - xMax - xMin) * scale * 0.5 : (xExtent - xMax - xMin) * scale * 0.5;
            translateY = yExtent * scale < this.height ? (this.nodeHeight * scale + this.height - (yExtent * scale)) / 2 : (this.nodeHeight * scale / 2);
        }

        this.applyTransform(translateX, translateY, scale);
    }

    updateBoxSize(width: number, height: number) {
        this.nodeWidth = width || this.nodeWidth;
        this.nodeHeight = height || this.nodeHeight;

        if (this.options.horizontalLayout) {
            this.tree.nodeSize([this.nodeHeight + 20, this.nodeWidth + 100]);
        }
        else {
            this.tree.nodeSize([this.nodeWidth + 60, this.nodeHeight + 40]);
        }
    }

    private drawLinks(layoutRoot: VdtNode<TModel>, source: VdtNode<TModel>) {
        // Update links
        let links = this.container.selectAll<SVGPathElement, VdtLink<TModel>>("path.vdtLink")
            .data(layoutRoot.links(), d => d.target.data.id);

        // Add new links
        // Transition new links from the source's
        // old position to the links final position
        links.style('fill-opacity', 0);

        let linkEnter = links
            .enter()
            .append<SVGPathElement>("path")
            .attr("class", d => `vdtLink ${d.target.data.class}`)
            .attr("d", d => {
                if (this.options.horizontalLayout) {
                    let o = { x: source.x, y: (source.y + this.nodeWidth / 2) };
                    return this.transitionElbowH({ source: o, target: o });
                }
                else {
                    let o = { x: source.x, y: (source.y + this.nodeHeight / 2) };
                    return this.transitionElbowV({ source: o, target: o });
                }
            })
            .attr("opacity", d => ValueDriverTree.normalizeProgressBarValue(d.target.data.progressBarValue!) + 0.1);

        // Update the old links positions (entered links are merged in so they animate into place too,
        // preserving the v3 behaviour where enter().append() folded new elements into the update selection).
        linkEnter.merge(links).transition()
            .duration(this.duration)
            .style('fill-opacity', 1)
            .attr("class", d => `vdtLink ${d.target.data.class}`)
            .attr("d", d => this.options.horizontalLayout ? this.elbowH(d) : this.elbowV(d));

        // Remove any links we don't need anymore
        // if part of the tree was collapsed
        // Transition exit links from their current position
        // to the source's new position
        links.exit<VdtLink<TModel>>()
            .transition()
            .duration(this.duration)
            .style('stroke-opacity', 0)
            .style('opacity', 0)
            .attr("d", () => {
                if (this.options.horizontalLayout) {
                    let o = { x: source.x, y: (source.y + this.nodeWidth / 2) };
                    return this.transitionElbowH({ source: o, target: o });
                }
                else {
                    let o = { x: source.x, y: (source.y + this.nodeHeight / 2) };
                    return this.transitionElbowV({ source: o, target: o });
                }
            })
            .remove();
    }

    public draw(source: ValueDriverTreeNode<TModel>) {
        const layoutRoot = this.computeLayout();
        const unmodifiedNodes = layoutRoot.descendants();
        const src = this.resolveSource(unmodifiedNodes, source);

        this.drawLinks(layoutRoot, src);

        // Update nodes
        let nodes = this.container.selectAll<SVGGElement, VdtNode<TModel>>("g.vdtNode")
            .data(unmodifiedNodes, node => node.data.id);

        // Add any new nodes
        let nodeEnter = nodes.enter().append<SVGGElement>("g")
            .attr('transform', node => {
                // Entering nodes grow out from their parent's PREVIOUS position, stashed on the data
                // (`data.x0`/`data.y0`) since the layout wrappers are rebuilt on every draw.
                if (node.parent && node.parent.data.y0 != null && node.parent.data.x0 != null) {
                    if (this.options.horizontalLayout) {
                        return `translate(${node.parent.data.y0},${node.parent.data.x0})`;
                    }
                    else {
                        return `translate(${node.parent.data.x0}, ${node.parent.data.y0})`;
                    }
                }
                return "";
            })
            .attr("data-title", node => node.data.title as string);

        // Draw the node boxes...
        nodeEnter.append<SVGForeignObjectElement>("foreignObject")
            .attr("x", -this.nodeWidth / 2)
            .attr("y", -this.nodeHeight / 2)
            .attr("width", this.nodeWidth)
            .attr("height", this.nodeHeight)
            .attr("overflow", "visible")
            .html(node => `<div id="node-${node.data.id}" class="vdtNodeContainer"></div>`);

        // Merge entered + existing nodes so class and position updates apply to both (v3 parity: in v3,
        // enter().append() folded the new elements into the update selection).
        let nodeUpdate = nodeEnter.merge(nodes);

        // Set the class attributes
        nodeUpdate.attr("class", node => {
            const classes = ["vdtNode"];
            classes.push(this.mode);
            classes.push(ValueDriverTree.normalizeProgressBarValue(node.data.progressBarValue ?? 1) < 0.6 ? "low-opacity" : "high-opacity");
            node.data.class && classes.push(node.data.class);
            return classes.join(" ");
        });

        // Update the position of both old and new nodes
        nodeUpdate.transition()
            .duration(this.duration)
            .attr("transform", d => {
                return this.options.horizontalLayout ?
                    "translate(" + d.y + "," + d.x + ")" :
                    "translate(" + d.x + "," + d.y + ")";
            });

        // Remove nodes we aren't showing anymore
        nodes.exit<VdtNode<TModel>>()
            .transition()
            .duration(this.duration)
            .style('fill-opacity', 0)
            .style('opacity', 0)
            .style('pointer-events', 'none') // Prevent tooltip from showing up for nodes in motion
            .attr("transform", () => {
                return this.options.horizontalLayout ?
                    "translate(" + src.y + "," + src.x + ")" :
                    "translate(" + src.x + "," + src.y + ")";
            })
            .remove();

        // Stash the old positions for transition (on the data, which persists across draws — the layout
        // wrappers do not).
        unmodifiedNodes.forEach(node => {
            node.data.x0 = node.x;
            node.data.y0 = node.y;
        });
    }

    private static normalizeProgressBarValue(value: number) {
        return Math.max(0, Math.min(1, value));
    }

    public updateNodeStatuses(collapsedMap: Object, isInitialRender = false) {
        let isDirty = false;

        const toggleNodesImpl = (node: ValueDriverTreeNode<any>) => {
            const collapsedStatusFromMap = collapsedMap?.[node.id];

            if (node.collapsed !== collapsedStatusFromMap) {
                node.collapsed = collapsedStatusFromMap;
                isDirty = true;
            }

            for (const child of (node?.nodes ?? [])) {
                toggleNodesImpl(child);
            }
        }

        toggleNodesImpl(this.root);

        if (isDirty && !isInitialRender) {
            this.draw(this.root);
            this.zoomToFit();
        }
    }

    public toggleAllNodes(mode: "expand" | "collapse") {
        const toggleAllNodesImpl = (node: ValueDriverTreeNode<any>) => {
            node.collapsed = mode === "collapse";

            if (mode === "collapse") {
                this.onCollapse && this.onCollapse(node);
            } else {
                this.onExpand && this.onExpand(node);
            }

            for (var child of (node?.nodes ?? [])) {
                toggleAllNodesImpl(child);
            }
        }

        toggleAllNodesImpl(this.root);

        this.draw(this.root);
        this.zoomToFit();
    }

    /**
     * Update a node's state when it is clicked.
     */
    private toggleNode(node: ValueDriverTreeNode<TModel>) {
        this.lastClickTarget = node;
        node.collapsed = !node.collapsed;

        if (node.collapsed) {
            this.onCollapse && this.onCollapse(node);
        } else {
            this.onExpand && this.onExpand(node);
        }

        this.draw(node);

        if (this.zoomToFitOnNodeToggle) {
            this.zoomToFit();
        }
    }

    public toggleNodeById(id: string) {
        // Locate the node in the current layout, then toggle the underlying DATA node (the layout
        // wrapper is throwaway; the children accessor reads `collapsed` off the data).
        const wrapper = this.computeLayout().descendants().find(n => n.data.id === id);
        if (wrapper) {
            this.toggleNode(wrapper.data);
        }
    }

    /**
     * Custom path function that creates straight connecting
     * lines. Calculate start and end position of links.
     * Instead of drawing to the center of the node,
     * draw to the border of the box.
     * That way drawing order doesn't matter. In other
     * words, if we draw to the center of the node
     * then we have to draw the links first and the
     * draw the boxes on top of them.
     */
    private elbowH(d: ElbowEnds) {
        let sourceX = d.source.x, sourceY = d.source.y + (this.nodeWidth / 2), targetX = d.target.x, targetY = d.target.y - (this.nodeWidth / 2);
        return "M" + sourceY + "," + sourceX
            + "H" + (sourceY + (targetY - sourceY) / 2)
            + "V" + targetX
            + "H" + targetY;
    }

    private elbowV(d: ElbowEnds) {
        const sourceX = d.source.x;
        let sourceY = d.source.y + (this.nodeHeight / 2);
        if ((d.source.children?.length ?? 0) > 0) sourceY += COLLAPSE_EXPAND_BUTTON_OVERFLOW_BOTTOM;

        const targetX = d.target.x;
        const targetY = d.target.y - (this.nodeHeight / 2);
        return "M" + sourceX + "," + sourceY
            + "V" + (sourceY + (targetY - sourceY) / 2)
            + "H" + targetX
            + "V" + targetY;
    }

    /**
     * Use a different elbow function for enter
     * and exit nodes. This is necessary because
     * the function above assumes that the nodes
     * are stationary along the x axis.
     */
    private transitionElbowH(d: ElbowEnds) {
        return "M" + d.source.y + "," + d.source.x
            + "H" + d.source.y
            + "V" + d.source.x
            + "H" + d.source.y;
    }

    private transitionElbowV(d: ElbowEnds) {
        return "M" + d.source.x + "," + d.source.y
            + "V" + d.source.y
            + "H" + d.source.x
            + "V" + d.source.y;
    }
}
